import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(
    () => {
      if (!article) return [];
      return articles
        .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
        .slice(0, 3);
    },
    [article],
  );

  const breadcrumbItems = useMemo(() => {
    if (!article) return [{ label: '首页', href: '/' }];
    return getArticleBreadcrumb(article.category, article.categorySlug, article.title);
  }, [article]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <article className="pt-20 pb-16 md:pb-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-md text-xs font-medium hover:bg-primary-200 transition-colors"
              >
                {article.category}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
              <span>{article.author}</span>
              <span className="text-foreground-400">·</span>
              <span>{article.publishDate}</span>
              <span className="text-foreground-400">·</span>
              <span>{article.readTime} 分钟阅读</span>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-700 rounded-full text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-500">{ra.readTime} 分钟阅读</span>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mt-1.5 leading-snug line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}